import React, { useCallback, useRef, useState, useMemo, useEffect } from "react";
import TextareaAutosize from "react-textarea-autosize";
import {
  FiPaperclip,
  FiSliders,
  FiCpu,
  FiCheck,
  FiEye,
  FiLoader,
  FiPause,
  FiPhone,
  FiMessageCircle
} from "react-icons/fi";
import { FaRegLightbulb, FaGlobeAsia, FaFileUpload, FaClock } from "react-icons/fa";
import { DataState } from "../context/DataProvider";
import { useMCP } from "../context/McpProvider";
import { RxCross2 } from "react-icons/rx";
import toast from "react-hot-toast";
import { deleteFile, downloadFile, getEmailSuggestions, uploadFilesFromDriveLink, API_URL } from "../services/api";
import { getReportTemplateSuggestions } from "../services/reportTemplateApi";
import Loader from "./custom/Loader";
import { IoSend, IoChevronUp } from "react-icons/io5";
import FileManagement from "./FileManagement";
import { FiDownload } from "react-icons/fi";
import { integrationData, triggers } from "../utils/constants";
import SelectFunctions from "./modals/SelectFunstions";
import GroupsModal from "./modals/GroupsModal";
import DataSourceModal from "./DataSourceModal";
import DataApps from "./DataSource/DataApps";
import { useLocation } from "react-router-dom";
import {
  collab,
  jiraBoard,
  jiraProject,
  jiraSprint,
  jirauser,
  repo,
  slackChannel,
  slackUser,
  confluenceSpace,
} from "../utils/msgTriggerData";
import ImageComponent from "./ImageComponent";
import { getFileIcon } from "./chat/AttachmentIcon";
import { BsPuzzle, BsRobot } from 'react-icons/bs';
import McpTaskSelector from "./McpConnections/McpTaskSelector";
import AgentArchitectureModal from "./modals/AgentArchitectureModal";
import { motion } from "framer-motion";
import PromptSelectorModal from "./modals/PromptSelectorModal";
import ExcelPreviewModal from "./chat/modals/ExcelPreviewModal";
import DocxPreviewModal from "./chat/PreviewModal";
import { TbPlugConnected } from "react-icons/tb";
import { X } from "lucide-react";
import IntegrationGuideNotification from "./integrations/IntegrationGuideNotification";
import useClickOutside from "../hooks/useClickOutside";
import Integration from "./Integration";
import { RiClaudeFill, RiGeminiFill } from "react-icons/ri";
import { TbBrandOpenai } from "react-icons/tb";

const IN_PRODUCTION = process.env.REACT_APP_IS_PROD === "true";

//Stop chat response btn
function LoadingPauseIcon() {
  return (
    <div className="relative flex items-center justify-center">
      <div
        className="relative flex items-center justify-center flex-none"
        style={{ width: 24, height: 24 }}
      >
        <motion.div
          className="absolute inset-0 rounded-full border-2 border-zinc-600 border-t-zinc-300"
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
        />
        <FiPause className="w-4 h-4 text-zinc-100 z-10" />
      </div>
    </div>
  );
}

export const getFileForPreview = async (accessToken, fileId) => {
  const options = {
    method: 'GET',
    headers: { 'Authorization': `Bearer ${accessToken}` },
    credentials: 'include'
  };

  try {
    const response = await fetch(`${API_URL}/file/download-direct/${fileId}`, options);

    if (response.status === 401) {
      localStorage.clear();
      window.location.href = '/login';
      return;
    }

    if (!response.ok) {
      throw new Error(`Error fetching file: ${response.statusText}`);
    }

    return response.blob();

  } catch (error) {
    console.error('File Preview Download Error:', error);
    throw error;
  }
};

const getModelIcon = (name) => {
  const lower = name.toLowerCase();

  if (/claude/i.test(lower))
    return <RiClaudeFill className="text-zinc-400 text-base" />;

  if (/gpt|openai/i.test(lower))
    return <TbBrandOpenai className="text-zinc-400 text-base" />;

  if (/gemini/i.test(lower))
    return <RiGeminiFill className="text-zinc-400 text-base" />;

  if (/oqlous/i.test(lower))
    return <img className="text-zinc-400 text-base h-4 w-4" src="../assets/logo.png" />;

  if (/deepseek/i.test(lower))
    return <img className="text-zinc-400 text-base h-4 w-4" src="../assets/deepseek.svg" />;

  return null;
};

const AutoResizableTextarea = ({
  setInput, input, handleSend,
  isDraggingOver = false,
  tempImages = [],
  setTempImages = () => { },
  justUploadedImages = false,
  setJustUploadedImages = () => { },
  processAndUploadFiles = () => { },
  newChat = false
}) => {

  const {
    showUploadModal,
    setShowUploadModal,
    files,
    setFiles,
    fetchFiles,
    userGoogleId,
    createTaskLoading,
    sendTaskLoading,
    sendSubTaskLoading,
    isBusy,
    currentTaskId,
    justSavedFiles,
    setJustSavedFiles,
    currentTaskDataSourceFiles: currentDataSources,
    selectedReason,
    setSelectedReason,
    isReasoning,
    setIsReasoning,
    isWebSearch,
    setIsWebSearch,
    isCanvas,
    setIsCanvas,
    isAgentMode, setIsAgentMode,
    agentSequence, setAgentSequence,
    agentTree, setAgentTree,
    taskMCPConnections, setTaskMCPConnections,
    taskOqlousTools, setTaskOqlousTools,
    taskManualToolSelection,
    currentTaskModel,
    setCurrentTaskModel,
    toolCallApprovalList,
    userInfo,
    stopStreaming,
    isStreaming
  } = DataState();

  const location = useLocation();

  //states
  const [isMcpSelectorOpen, setIsMcpSelectorOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [isloading, setIsloading] = useState(false);
  const [showMobileOptions, setShowMobileOptions] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [openPromptSelector, setOpenPromptSelector] = useState(false);
  const [isModelSelectorOpen, setIsModelSelectorOpen] = useState(false);
  const [error, setError] = useState(null);
  const [reasonEnabled, setReasonEnabled] = useState(false);
  const [isAgentModalOpen, setIsAgentModalOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [processingFileId, setProcessingFileId] = useState(null);
  const [selectedValue, setSelectedValue] = useState(integrationData[2]);
  const [isModalOpen, setModalOpen] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeTrigger, setActiveTrigger] = useState(null);
  const [activeTriggerInfo, setActiveTriggerInfo] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [excelPreviewModal, setExcelPreviewModal] = useState({ isOpen: false, file: null });
  const [showGroups, setShowGroups] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [driveLinks, setDriveLinks] = useState([]); // { id, url, status: 'downloading'|'done'|'error', filename? }
  const [showIntegrations, setShowIntegrations] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null); // { _id, template_name }

  //refs
  const modelSelectorRef = useRef(null);
  const walletAddressRef = useRef(null);
  const mobileOptionsRef = useRef(null);
  const textareaRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceTimeoutRef = useRef(null);
  const scrollRef = useRef(null);
  const DEBOUNCE_DELAY = 500;
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  //custom hooks
  useClickOutside([dropdownRef], () => toggleDropdown(null));
  useClickOutside([mobileOptionsRef], () => setShowMobileOptions(false));
  useClickOutside([walletAddressRef], () => setShowUploadModal(false));
  useClickOutside([modelSelectorRef], () => setIsModelSelectorOpen(false));

  const openModal = () => setModalOpen(true);
  const closeModal = () => setModalOpen(false);

  // Inside your AutoResizableTextarea component
  const handleTurnOffAgentMode = (e) => {
    // This prevents the main button's onClick from firing as well
    e.stopPropagation();
    setIsAgentMode(false);
    toast('Agent Mode Deactivated', {
      icon: '🤖',
      style: {
        borderRadius: '10px',
        background: '#333',
        color: '#fff',
      },
    }
    );
  };

  // 3. Create a handler to open the agent modal
  const handleAgentButtonClick = () => {
    if (isAgentMode) {
      // If agent mode is already on, open the modal for control
      setIsAgentModalOpen(true);
    } else {
      // Otherwise, just turn agent mode on
      setIsAgentMode(true);
      toast.success("Agent Mode Activated");
    }
  };

  const onDragStart = (e) => {
    setIsDragging(true);
    scrollRef.current.classList.add("cursor-grabbing");
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  };

  const onDragMove = (e) => {
    if (!isDragging) return;
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 1.5; // scroll speed multiplier
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };

  const onDragEnd = () => {
    setIsDragging(false);
    scrollRef.current.classList.remove("cursor-grabbing");
  };

  // When an option is selected, update and close dropdown.
  const handleReasonSelect = (level) => {
    if (level === "None") {
      setSelectedReason(null);
    } else {
      setSelectedReason(level);
    }
    setReasonEnabled(false);
  };

  // Toggle dropdown; if already selected and dropdown is closed, clear the selection.
  const toggleReasonDropdown = () => {
    if (!reasonEnabled && selectedReason) {
      // Clear reason if already active and dropdown is not open.
      setSelectedReason(null);
    } else {
      setReasonEnabled(!reasonEnabled);
    }
  };

  const [showDataSource, setShowDataSource] = useState(false);
  const closeDataSourceModal = () => {
    setShowDataSource(false);
  };

  // Regex to match Google Drive and Google Docs URLs
  const GOOGLE_DRIVE_URL_REGEX = /https?:\/\/(?:drive\.google\.com\/(?:file\/d\/|open\?id=|uc\?(?:export=download&)?id=)|docs\.google\.com\/(?:document|spreadsheets|presentation)\/d\/)[^\s]+/gi;

  // Download files from Google Drive links
  const handleDriveLinksDownload = async (urls) => {
    const newLinks = urls.map((url, i) => ({
      id: Date.now() + i,
      url,
      status: 'downloading',
      filename: null
    }));
    setDriveLinks(prev => [...prev, ...newLinks]);

    try {
      const response = await uploadFilesFromDriveLink(
        userGoogleId, currentTaskId, urls
      );

      const filesInfo = response?.files_info || [];
      setDriveLinks(prev =>
        prev.map(link => {
          const match = newLinks.find(nl => nl.id === link.id);
          if (match) {
            const fileInfo = filesInfo.find(f => f.file_id || f.filename);
            return {
              ...link,
              status: 'done',
              filename: fileInfo?.original_filename || fileInfo?.filename || 'File'
            };
          }
          return link;
        })
      );

      toast.success(`${filesInfo.length} file(s) downloaded from Google Drive`);
      setJustSavedFiles(true);

      // Auto-remove done chips after 3 seconds
      setTimeout(() => {
        setDriveLinks(prev => prev.filter(l => !newLinks.find(nl => nl.id === l.id)));
      }, 3000);

    } catch (err) {
      console.error('Drive link download error:', err);
      setDriveLinks(prev =>
        prev.map(link =>
          newLinks.find(nl => nl.id === link.id)
            ? { ...link, status: 'error' }
            : link
        )
      );
      const errorMsg = typeof err === 'string' ? err : (err?.message || 'Failed to download from Google Drive');
      toast.error(errorMsg);
    }
  };

  // UPDATED: handlePaste now detects Google Drive links and also handles files
  const handlePaste = (event) => {
    const clipboardFiles = event.clipboardData.files;
    if (clipboardFiles && clipboardFiles.length > 0) {
      event.preventDefault();
      processAndUploadFiles(clipboardFiles);
      return;
    }

    // Check for Google Drive URLs in pasted text
    const pastedText = event.clipboardData.getData('text');
    if (pastedText) {
      const driveUrls = pastedText.match(GOOGLE_DRIVE_URL_REGEX);
      if (driveUrls && driveUrls.length > 0) {
        event.preventDefault();
        // Remove the Drive URLs from the text and keep the rest
        let remainingText = pastedText;
        driveUrls.forEach(url => {
          remainingText = remainingText.replace(url, '').trim();
        });
        if (remainingText) {
          setInputValue(prev => prev + remainingText);
          // setInput(prev => prev + remainingText);
        }
        handleDriveLinksDownload(driveUrls);
      }
    }
  };

  const handleKeyDown = (e) => {
    if (
      !isloading &&
      !createTaskLoading &&
      !sendTaskLoading &&
      !sendSubTaskLoading &&
      !isBusy &&
      !((toolCallApprovalList?.length || 0) > 0) &&
      e.key === "Enter" &&
      !e.shiftKey &&
      !e.ctrlKey &&
      !e.altKey &&
      !e.metaKey
    ) {
      e.preventDefault(); // Prevent the default newline insert
      // Prepend template context if a template is selected
      let messageToSend = inputValue;
      if (selectedTemplate) {
        messageToSend = `[Using template: "${selectedTemplate.template_name}" (template_id: ${selectedTemplate._id})]\n${input}`;
        setSelectedTemplate(null);
      }

      handleSend(messageToSend);

      // ✅ update global ONLY here
      // setInput(messageToSend);
      setInputValue("");
      setInput("");
    }
  };

  const handleDownload = async (fileId) => {
    // +++ Set the processing ID at the start +++
    setProcessingFileId(fileId);
    try {
      const response = await downloadFile(fileId, userGoogleId);
      if (!response.error) {
        window.open(response.url, "_blank");
      } else {
        setError(response.error);
      }
    } catch (error) {
      setError("Error downloading file");
    } finally {
      // +++ Clear the processing ID at the end +++
      setProcessingFileId(null);
    }
  };

  const handleDelete = async (fileId) => {
    // +++ Set the processing ID at the start +++
    setProcessingFileId(fileId);
    try {
      const response = await deleteFile(fileId, userGoogleId);
      if (response.error) {
        setError(response.error);
      } else {
        toast.success("File deleted successfully");
        setFiles(files.filter((file) => file.file_id != fileId));
        setError(null);
      }
    } catch (error) {
      console.error("Error deleting file:", error);
      setError("Error deleting file");
    } finally {
      // +++ Clear the processing ID at the end +++
      setProcessingFileId(null);
    }
  };

  const toggleDropdown = (botId) => {
    setDropdownOpen((prev) => (prev === botId ? null : botId));
  };

  const handleInputChange = (event) => {
    const value = event.target.value;
    setInputValue(value);
    // setInput(value);

    // Get the word that contains the cursor position
    const cursorPosition = event.target.selectionStart;
    const words = value.slice(0, cursorPosition).split(" ");
    const currentWord = words[words.length - 1];

    const textBeforeCursor = value.slice(0, cursorPosition);
    const lastSpaceIndex = textBeforeCursor.lastIndexOf(' ');
    const lastNewlineIndex = textBeforeCursor.lastIndexOf('\n');
    const wordSegmentStartIndex = Math.max(lastSpaceIndex, lastNewlineIndex) + 1;

    const currentWordSegment = textBeforeCursor.slice(wordSegmentStartIndex);

    // Trim only leading spaces from the current segment to find the actual word start
    const trimmedWordAtCursor = currentWordSegment.trimStart();

    const matchedTrigger = Object.keys(triggers)
      .sort((a, b) => b.length - a.length)
      .find((trigger) => trimmedWordAtCursor.startsWith(trigger));

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    if (matchedTrigger && trimmedWordAtCursor.length >= matchedTrigger.length) {
      const actualWordStartIndex = wordSegmentStartIndex + (currentWordSegment.length - trimmedWordAtCursor.length);

      setActiveTrigger(matchedTrigger);
      setActiveTriggerInfo({
        startIndex: actualWordStartIndex,
        originalWordText: trimmedWordAtCursor,
        trigger: matchedTrigger,
      });

      // Calculate dropdown position relative to viewport
      if (textareaRef.current) {
        const rect = textareaRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.top - 8, // 8px gap above textarea
          left: rect.left,
        });
      }
      const query = currentWord.trim().slice(matchedTrigger.length).toLowerCase();

      const triggerConfig = triggers[matchedTrigger];

      if (typeof triggerConfig.data === "function") {
        if (query.length >= 0) {
          debounceTimeoutRef.current = setTimeout(() => {
            // Call your performApiSuggestionFetch, which internally calls the configured function
            performApiSuggestionFetch(
              userGoogleId,
              query, // Pass raw query
              triggerConfig.data // Pass the fetch function from config
            );
          }, DEBOUNCE_DELAY);
        } else {
          // Query is empty/too short for API call, clear suggestions
          setFilteredData([]);
          setShowDropdown(false);
        }
      } else {
        const lowercaseQuery = query.toLowerCase(); // Use lowercase for static filtering
        const filtered = triggerConfig.data.filter((item) => {
          const searchValue = String(
            item[triggerConfig.mainField]
          ).toLowerCase();
          return searchValue.includes(lowercaseQuery);
        });
        setFilteredData(filtered);
        setShowDropdown(filtered.length > 0);
      }
    } else {
      setActiveTrigger(null);
      setShowDropdown(false);
      setActiveTriggerInfo(null);
      setFilteredData([]);
    }
  };

  const handleItemClick = (item) => {
    if (!activeTriggerInfo || !activeTrigger) {
      setShowDropdown(false);
      return;
    };

    const { startIndex, originalWordText } = activeTriggerInfo;
    const triggerConfig = triggers[activeTrigger];
    const selectedValue = item[triggerConfig.mainField];

    // For report-template type, store as a capsule chip instead of inline text
    if (triggerConfig.type === "report-template" && item._id) {
      setSelectedTemplate({ _id: item._id, template_name: selectedValue });
      // Remove the trigger text from textarea  
      const prefix = inputValue.slice(0, startIndex);
      const suffix = inputValue.slice(startIndex + originalWordText.length);
      const finalNewValue = `${prefix}${suffix}`.trim();
      setInputValue(finalNewValue);
      // setInput(finalNewValue);
      setShowDropdown(false);
      setActiveTrigger(null);
      setActiveTriggerInfo(null);
      if (textareaRef.current) textareaRef.current.focus();
      return;
    }

    const prefix = inputValue.slice(0, startIndex);
    const suffix = inputValue.slice(startIndex + originalWordText.length);

    const finalNewValue = `${prefix}${selectedValue} ${suffix}`;
    setInputValue(finalNewValue);
    // setInput(finalNewValue);
    setShowDropdown(false);
    setActiveTrigger(null);
    setActiveTriggerInfo(null);

    // Focus the textarea and set the cursor position correctly
    if (textareaRef.current) {
      textareaRef.current.focus();
      setTimeout(() => {
        if (textareaRef.current) {
          const newCursorPos = prefix.length + selectedValue.length + 1;
          textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
        }
      }, 0);
    }
  };

  const performApiSuggestionFetch = useCallback(
    async (token, queryForApi, fetchFunction) => {
      setIsloading(true);
      try {
        const suggestionsResultResponse = await fetchFunction(
          token,
          queryForApi
        );
        let suggestionsResult = [];
        if (Array.isArray(suggestionsResultResponse)) {
          suggestionsResult = suggestionsResultResponse;
        } else if (suggestionsResultResponse?.suggestions) {
          suggestionsResult = suggestionsResultResponse.suggestions;
        } else if (suggestionsResultResponse?.data && Array.isArray(suggestionsResultResponse.data)) {
          suggestionsResult = suggestionsResultResponse.data;
        }

        if (Array.isArray(suggestionsResult)) {
          setFilteredData(suggestionsResult);
          setShowDropdown(suggestionsResult.length > 0);
        } else {
          setFilteredData([]);
          setShowDropdown(false);
          if (suggestionsResult !== undefined) {
            console.warn(
              `API fetch failed for query '${queryForApi}': unexpected data:`,
              suggestionsResult
            );
          }
        }
      } catch (error) {
        console.error(`API fetch failed for query '${queryForApi}':`, error);
        setFilteredData([]);
        setShowDropdown(false);
      } finally {
        setIsloading(false);
      }
    },
    [setIsloading, setFilteredData, setShowDropdown]
  );

  const renderDropdownItem = (item, index) => {
    if (!activeTrigger) return null;

    const triggerConfig = triggers[activeTrigger];
    const mainValue = item[triggerConfig.mainField];

    // Custom UI based on trigger type
    const renderContent = () => {
      switch (triggerConfig.type) {
        case "slack-user":
          return (
            <div className="flex flex-col">
              <span className="font-medium">@{mainValue}</span>
              <span className="text-xs text-zinc-400">{item.RealName}</span>
            </div>
          );
        case "slack-user":
          return (
            <div className="flex flex-col">
              <span className="font-medium">@{mainValue}</span>
              <span className="text-xs text-zinc-400">{item.RealName}</span>
            </div>
          );

        case "slack-channel":
          return (
            <div className="flex flex-col">
              <span className="font-medium">#{mainValue}</span>
              <span className="text-xs text-zinc-400 truncate max-w-md">
                {item.Members} members
              </span>
            </div>
          );

        case "jira-sprint":
          return (
            <div className="flex flex-col">
              <span className="font-medium">{mainValue}</span>
              <span className="text-xs text-zinc-400">
                {item.State} • {item.StartDate} to {item.EndDate}
              </span>
            </div>
          );

        case "jira-board":
          return (
            <div className="flex flex-col">
              <span className="font-medium">{mainValue}</span>
              <span className="text-xs text-zinc-400">
                {item.ProjectKey} • {item.Type}
              </span>
            </div>
          );

        case "repository":
          return (
            <div className="flex flex-col">
              <span className="font-medium">{mainValue}</span>
              <span className="text-xs text-zinc-400">
                {item.Collaborators?.length || 0} collaborators
              </span>
            </div>
          );

        case "collaborator":
          return (
            <div className="flex flex-col">
              <span className="font-medium">{mainValue}</span>
            </div>
          );

        case "jira-user":
          return (
            <div className="flex flex-col">
              <span className="font-medium">{mainValue}</span>
              <span className="text-xs text-zinc-400">{item.email}</span>
            </div>
          );
        case "confluence-space":
          return (
            <div className="flex flex-col">
              <span className="font-medium">{item.name}</span>
              <span className="text-xs text-zinc-400">{item.key}</span>
            </div>
          );

        case "report-template":
          return (
            <div className="flex flex-col">
              <span className="font-medium">{mainValue}</span>
              <span className="text-xs text-zinc-400">
                {item.template_type} • {item.section_count} sections
              </span>
            </div>
          );

        default:
          return (
            <div className="flex flex-col">
              <span className="font-medium">{mainValue}</span>
              {triggerConfig.displayFields.map((field) => (
                <span key={field} className="text-xs text-zinc-400">
                  {item[field]}
                </span>
              ))}
            </div>
          );
      }
    };

    return (
      <li
        key={index}
        onClick={() => handleItemClick(item)}
        className="px-6 py-3 hover:bg-zinc-700 cursor-pointer text-sm flex items-center gap-2"
      >
        {renderContent()}
      </li>
    );
  };

  // Mobile Tools Component
  const MobileToolsDropup = () => (
    <div
      ref={mobileOptionsRef}
      className="absolute bottom-full left-0 mb-2 bg-zinc-800 rounded-lg border border-zinc-700 shadow-lg p-2 z-50 min-w-[180px]"
    >
      <div className="flex flex-col gap-1">
        <button
          disabled={sendTaskLoading}
          onClick={() => {
            setIsReasoning(!isReasoning);
            setShowMobileOptions(false);
          }}
          className={`flex items-center gap-2 rounded-md px-3 py-2 transition-all duration-200 text-sm w-full text-left disabled:opacity-50 disabled:pointer-events-none ${isReasoning
            ? "bg-primary_c border border-primary_c text-white"
            : "hover:bg-zinc-700 text-zinc-300"
            }`}
        >
          <FaRegLightbulb className="text-sm" />
          <span>Reason</span>
        </button>
        <button
          disabled={sendTaskLoading}
          onClick={() => {
            setIsWebSearch(!isWebSearch);
            setShowMobileOptions(false);
          }}
          className={`flex items-center gap-2 rounded-md px-3 py-2 transition-all duration-200 text-sm w-full text-left  disabled:opacity-50 disabled:pointer-events-none ${isWebSearch
            ? "bg-primary_c border border-primary_c text-white"
            : "hover:bg-zinc-700 text-zinc-300"
            }`}
        >
          <FaGlobeAsia className="text-sm" />
          <span>Web</span>
        </button>
        {!IN_PRODUCTION &&
          <div className="relative"> {/* Wrap in relative container */}
            <button
              disabled={sendTaskLoading}
              onClick={() => {
                handleAgentButtonClick();
                setShowMobileOptions(false);
              }}
              className={`flex items-center justify-between gap-2 rounded-md px-3 py-2 transition-all duration-200 text-sm w-full text-left  disabled:opacity-50 disabled:pointer-events-none ${isAgentMode
                ? "bg-primary_c border border-primary_c text-white"
                : "hover:bg-zinc-700 text-zinc-300"
                }`}
            >
              <div className="flex items-center gap-2">
                <BsRobot className="text-sm" />
                <span>Agents</span>
              </div>
            </button>

            {/* The 'X' button for mobile */}
            {isAgentMode && (
              <button
                disabled={sendTaskLoading}
                onClick={(e) => {
                  handleTurnOffAgentMode(e);
                  setShowMobileOptions(false);
                }}
                className="absolute top-1/2 right-2 -translate-y-1/2 p-1 bg-black/20 hover:bg-black/40 rounded-full transition-colors  disabled:opacity-50 disabled:pointer-events-none "
                title="Turn off Agent Mode"
              >
                <RxCross2 size={16} />
              </button>
            )}
          </div>
        }
        {/* MODIFIED: Mobile Model Selector with descriptions */}
        {userInfo?.available_models?.length > 0 && (
          <>
            <div className="my-1 border-t border-zinc-700" />
            <div className="relative" ref={modelSelectorRef}>
              <button
                disabled={sendTaskLoading}
                onClick={() => setIsModelSelectorOpen(!isModelSelectorOpen)}
                className="flex items-center justify-between gap-2 rounded-md px-3 py-2 transition-all duration-200 text-sm w-full text-left hover:bg-zinc-700 text-zinc-300 disabled:opacity-50 disabled:pointer-events-none"
              >
                <div className="flex items-center gap-2">
                  <FiCpu className="text-sm" />
                  <span>{currentTaskModel || "Model"}</span>
                </div>
                <IoChevronUp className={`text-sm transition-transform duration-200 ${isModelSelectorOpen ? 'rotate-0' : 'rotate-180'}`} />
              </button>
              {isModelSelectorOpen && (
                <div className="absolute bottom-0 left-full ml-2 w-64 bg-zinc-900 border border-zinc-700 rounded-lg shadow-lg z-50">
                  <ul className="max-h-60 overflow-y-auto">
                    <li title={userInfo?.default_model?.description || 'The default Oqlous model.'}>
                      <button
                        onClick={() => {
                          setCurrentTaskModel(null);
                          setIsModelSelectorOpen(false);
                          setShowMobileOptions(false);
                        }}
                        className={`w-full text-left px-4 py-2.5 flex items-center justify-between hover:bg-zinc-700 transition-colors ${currentTaskModel === null ? 'text-blue-400' : 'text-zinc-300'}`}
                      >
                        <div className="flex flex-col">
                          <span className={`font-semibold text-xs ${currentTaskModel === null ? 'text-blue-400' : 'text-zinc-200'}`}>
                            {userInfo?.default_model?.name || "Oqlous"}
                          </span>
                          {userInfo?.default_model?.description && (
                            <span className="text-xs text-zinc-400 mt-0.5 font-normal whitespace-pre-wrap">
                              {userInfo.default_model.description}
                            </span>
                          )}
                        </div>
                        {currentTaskModel === null && <FiCheck className="text-blue-400 self-center" />}
                      </button>
                    </li>
                    {userInfo.available_models.map((model, index) => (
                      <li key={model.name || index} title={model.description}>
                        <button
                          onClick={() => {
                            setCurrentTaskModel(model.name);
                            setIsModelSelectorOpen(false);
                            setShowMobileOptions(false);
                          }}
                          className={`w-full text-left px-4 py-2.5 flex items-center justify-between hover:bg-zinc-700 transition-colors ${currentTaskModel === model.name ? 'text-blue-400' : 'text-zinc-300'}`}
                        >
                          <div className="flex flex-col">
                            {/* <span className={`font-semibold text-xs ${currentTaskModel === model.name ? 'text-blue-400' : 'text-zinc-200'}`}>{model.name}</span> */}
                            {/* --- MODIFICATION START --- */}
                            <div className="flex items-center">
                              <span className={`font-semibold text-xs ${currentTaskModel === model.name ? 'text-blue-400' : 'text-zinc-200'}`}>
                                {model.name}
                              </span>
                              {model.is_new && (
                                // --- MODIFICATION START ---
                                <span className="ml-2 -mt-px inline-flex items-center rounded-md border border-yellow-500 px-1 text-xs font-medium text-yellow-500">
                                  NEW
                                </span>
                                // --- MODIFICATION END ---
                              )}
                            </div>
                            {/* --- MODIFICATION END --- */}
                            {model.description && (
                              <span className="text-xs text-zinc-400 mt-0.5 font-normal whitespace-pre-wrap">
                                {model.description}
                              </span>
                            )}
                          </div>
                          {currentTaskModel === model.name && <FiCheck className="text-blue-400 self-center" />}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </>
        )}

      </div>
    </div>
  );


  //Excel preview handler
  const handleExcelPreview = async (file) => {
    setProcessingFileId(file.file_id);

    try {
      const blob = await getFileForPreview(userGoogleId, file.file_id);

      const fileObject = new File(
        [blob],
        file.filename,
        { type: blob.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
      );

      setExcelPreviewModal({
        isOpen: true,
        file: fileObject
      });

    } catch (error) {
      toast.error("Could not load preview.");
      console.error(error);
    } finally {
      setProcessingFileId(null);
    }
  };

  const closeExcelPreview = () => {
    setExcelPreviewModal({
      isOpen: false,
      file: null
    });
  };

  //Docx preview handler
  const handleDocxPreview = async (file) => {
    // +++ Set the processing ID at the start +++
    setProcessingFileId(file.file_id);
    try {
      console.log("Fetching file for preview:", file);
      const blob = await getFileForPreview(userGoogleId, file.file_id);
      const fileObject = new File([blob], file.filename, {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      });
      console.log("File object created for preview:", fileObject);
      setPreviewFile(fileObject);
      setIsPreviewOpen(true);
    } catch (error) {
      toast.error("Could not load preview.");
      console.error(error);
    } finally {
      // +++ Clear the processing ID at the end +++
      setProcessingFileId(null);
    }
  };

  // Check if mobile on mount and window resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    setInputValue(input || "");
  }, [input]);

  useEffect(() => {
  if (newChat) {
    setInputValue("");
  }
}, [newChat]);

  return (
    <>
      <div
        className="flex flex-col gap-2 rounded-2xl relative max-w-[750px] w-full mx-auto bg-zinc-800 border border-zinc-700"
      >
        <div
          className="flex flex-col gap-2 rounded-2xl relative max-w-[750px] w-full mx-auto bg-white bg-opacity-5 border border-zinc-700"
        >

          {/* Image display */}
          <ImageComponent
            tempImages={tempImages}
            justUploadedImages={justUploadedImages}
            setJustUploadedImages={setJustUploadedImages}
          />

          {/* Google Drive link download chips */}
          {driveLinks.length > 0 && (
            <div className="flex gap-2 px-2 pt-2 pb-1 flex-wrap">
              {driveLinks.map((link) => (
                <div
                  key={link.id}
                  className={`inline-flex items-center gap-2 h-[36px] rounded-lg px-3 py-1 text-xs border backdrop-blur-sm transition-all duration-200
                  ${link.status === 'downloading'
                      ? 'bg-blue-900/30 border-blue-600/40 text-blue-300'
                      : link.status === 'done'
                        ? 'bg-green-900/30 border-green-600/40 text-green-300'
                        : 'bg-red-900/30 border-red-600/40 text-red-300'
                    }`}
                >
                  <img
                    src="https://res.cloudinary.com/dsguoq6ad/image/upload/v1726229007/mentorEducationImages/lhupskidmwtibynwa0no.png"
                    className="h-4 w-4 flex-shrink-0"
                    alt="Google Drive"
                  />
                  <span className="truncate max-w-[180px]">
                    {link.status === 'downloading'
                      ? 'Downloading from Drive...'
                      : link.status === 'done'
                        ? link.filename
                        : 'Download failed'}
                  </span>
                  {link.status === 'downloading' && (
                    <FiLoader className="animate-spin w-3 h-3 flex-shrink-0" />
                  )}
                  {link.status === 'done' && (
                    <FiCheck className="w-3 h-3 flex-shrink-0" />
                  )}
                  {link.status === 'error' && (
                    <button
                      onClick={() => setDriveLinks(prev => prev.filter(l => l.id !== link.id))}
                      className="hover:text-red-200 transition-colors"
                    >
                      <RxCross2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Files display */}
          {files?.length > 0 && (
            <ul
              ref={scrollRef}
              className="flex gap-2 px-2 pt-2 pb-1 overflow-x-auto whitespace-nowrap no-scrollbar"
              onMouseDown={onDragStart}
              onMouseUp={onDragEnd}
              onMouseMove={onDragMove}
              onMouseLeave={onDragEnd}
            >
              {files.map((file) => {
                const filename = file.filename.toLowerCase();
                const isDocx = filename.endsWith('.docx');
                const isExcel = filename.endsWith('.xlsx') || filename.endsWith('.xls') || filename.endsWith('.csv');
                const isPreviewable = isDocx || isExcel;
                const isProcessingThisFile = processingFileId === file.file_id;
                const isAnyFileProcessing = processingFileId !== null;

                return (
                  <li
                    key={file.file_id}
                    className="
                    inline-block flex-shrink-0
                    flex items-center gap-2
                    h-[52px] min-w-[200px] max-w-[260px] 
                    rounded-lg px-3 py-2
                    bg-zinc-800/60 border border-zinc-700/50
                    backdrop-blur-sm
                    relative group 
                    hover:bg-zinc-700/60 hover:border-zinc-600/50
                    transition-all duration-200
                    shadow-sm hover:shadow-md
                  "
                  >
                    {/* Close button */}
                    <button
                      onClick={() => handleDelete(file.file_id)}
                      className="
                      absolute -top-1.5 -right-1.5 
                      bg-zinc-600 hover:bg-zinc-500
                      text-zinc-200 hover:text-white
                      rounded-full p-1
                      transition-all duration-200
                      shadow-md hover:shadow-lg
                      opacity-0 group-hover:opacity-100
                      z-10
                    "
                      aria-label="Remove file"
                    >
                      <RxCross2 className="w-3 h-3" />
                    </button>

                    {/* File icon with background */}
                    <div className="
                      flex-shrink-0 
                      bg-zinc-700/50 
                      rounded-md 
                      p-1.5 
                      flex items-center justify-center 
                      w-9 h-9
                      group-hover:bg-zinc-600/50
                      transition-colors duration-200
                    ">
                      <span className="text-lg text-zinc-300">
                        {getFileIcon(file.original_filename)}
                      </span>
                    </div>

                    {/* File details */}
                    <div className="flex-1 min-w-0 flex flex-col justify-center gap-0.5">
                      <span className="text-xs text-zinc-100 truncate font-medium leading-tight">
                        {file?.filename || file?.original_filename}
                      </span>
                      <span className="text-[10px] text-zinc-400 font-normal">
                        {file?.size ? `${(file.size / 1024).toFixed(1)} KB` : '-- KB'}
                      </span>
                    </div>

                    {/* Action button - Shows on hover */}
                    <div className="
                    flex-shrink-0 
                    opacity-0 group-hover:opacity-100 
                    transition-opacity duration-200
                  ">
                      {isPreviewable ? (
                        <button
                          onClick={() => {
                            if (isDocx) {
                              handleDocxPreview(file);
                            } else if (isExcel) {
                              handleExcelPreview(file);
                            }
                          }}
                          disabled={isAnyFileProcessing}
                          className="
                          text-zinc-300 hover:text-zinc-100
                          bg-zinc-700/50 hover:bg-zinc-600/50
                          rounded-md p-1.5
                          disabled:cursor-not-allowed disabled:opacity-40
                          transition-all duration-200
                          border border-zinc-600/30
                        "
                          title={isDocx ? "Preview Document" : "Preview Spreadsheet"}
                        >
                          {isProcessingThisFile ? (
                            <FiLoader className="animate-spin w-3.5 h-3.5" />
                          ) : (
                            <FiEye className="w-3.5 h-3.5" />
                          )}
                        </button>
                      ) : (
                        <button
                          onClick={() => handleDownload(file.file_id)}
                          disabled={isAnyFileProcessing}
                          className="
                          text-zinc-300 hover:text-zinc-100
                          bg-zinc-700/50 hover:bg-zinc-600/50
                          rounded-md p-1.5
                          disabled:cursor-not-allowed disabled:opacity-40
                          transition-all duration-200
                          border border-zinc-600/30
                        "
                          title="Download File"
                        >
                          {isProcessingThisFile ? (
                            <FiLoader className="animate-spin w-3.5 h-3.5" />
                          ) : (
                            <FiDownload className="w-3.5 h-3.5" />
                          )}
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          <div className="w-full flex flex-col items-center justify-center rounded-lg p-4 gap-3">

            <div className="w-full relative">
              {/* Selected template capsule chip */}
              {selectedTemplate && (
                <div className="flex items-center mb-2">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium text-white border border-[#5652CF]/50" style={{ backgroundColor: 'rgba(86, 82, 207, 0.3)' }}>
                    <span className="text-[#a5a2f7]">📄</span>
                    <span>{selectedTemplate.template_name}</span>
                    <button
                      onClick={() => setSelectedTemplate(null)}
                      className="ml-1 hover:bg-white/10 rounded-full p-0.5 transition-colors"
                      title="Remove template"
                    >
                      <RxCross2 size={12} />
                    </button>
                  </div>
                </div>
              )}
              <TextareaAutosize
                ref={textareaRef}
                minRows={1}
                maxRows={10}
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                className="flex-1 min-h-full resize-none bg-transparent outline-none text-p-text text-sm p-1 pb-0 w-full"
                placeholder="Enter Prompt to Execute"
              />
            </div>

            {/* Button Row */}
            <div className="w-full flex items-center justify-between relative mt-3">

              <div className="flex gap-2 items-center">
                {showMobileOptions && isMobile && <MobileToolsDropup />}

                {location.pathname === "/chat" && (
                  <button
                    disabled={sendTaskLoading}
                    onClick={() => setShowUploadModal(true)}
                    className={`rounded-full h-8 w-8 flex justify-center items-center text-5xl transition-all duration-300 flex-shrink-0 disabled:opacity-50  disabled:pointer-events-none ${isloading
                      ? "bg-opacity-40"
                      : "hover:bg-gray-300 hover:text-black"
                      }`}
                  >
                    <FiPaperclip className="text-base" />
                  </button>
                )}
                <button
                  onClick={(e) => { e.stopPropagation(); setShowIntegrations(true); }}
                  className={`rounded-full h-8 w-8 flex justify-center items-center text-5xl transition-all duration-300 flex-shrink-0 disabled:opacity-50  disabled:pointer-events-none hover:bg-gray-300 hover:text-black`}
                >
                  <TbPlugConnected className="text-base" />
                </button>



                {/* MCP button */}
                <div className="relative flex-shrink-0">
                  <button
                    disabled={sendTaskLoading}
                    onClick={() => setIsMcpSelectorOpen(true)}
                    title="Select Connections & Tools"
                    className={`rounded-full h-8 w-8 flex justify-center items-center text-5xl transition-all duration-300 disabled:opacity-50  disabled:pointer-events-none ${(taskManualToolSelection)
                      ? "text-blue-400 bg-blue-900/50 hover:bg-blue-900/80"
                      : "hover:bg-gray-300 hover:text-black"
                      }`}
                  >
                    <BsPuzzle className="text-base" />
                  </button>

                  {taskManualToolSelection && (
                    <span className="absolute top-0 right-0 bg-primary_c text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                      {(taskOqlousTools?.length || 0) + (taskMCPConnections?.length || 0)}
                    </span>
                  )}
                </div>

                {/* Desktop Options */}
                <div className="hidden md:flex gap-2 items-center">
                  <button
                    disabled={sendTaskLoading}
                    onClick={() => {
                      setIsReasoning(!isReasoning);
                    }}
                    className={`flex items-center gap-1 rounded-full px-2 h-7 transition-all duration-300 border  disabled:opacity-50 disabled:pointer-events-none ${isReasoning
                      ? "bg-primary_c border-primary_c text-white"
                      : "bg-transparent border-zinc-600 hover:bg-zinc-600 hover:text-white"
                      }`}
                  >
                    <FaRegLightbulb className="text-base" />
                    <span className="text-xs">Reason</span>
                  </button>

                  <button
                    disabled={sendTaskLoading}
                    onClick={() => {
                      setIsWebSearch(!isWebSearch);
                    }}
                    className={`flex items-center gap-1 rounded-full px-2 h-7 transition-all duration-300 border  disabled:opacity-50 disabled:pointer-events-none ${isWebSearch
                      ? "bg-primary_c border-primary_c text-white"
                      : "bg-transparent border-zinc-600 hover:bg-zinc-600 hover:text-white"
                      }`}
                  >
                    <FaGlobeAsia className="text-base" />
                    <span className="text-xs">Web</span>
                  </button>

                  {!IN_PRODUCTION &&
                    <div className="relative ">
                      <button
                        disabled={sendTaskLoading}
                        onClick={handleAgentButtonClick}
                        className={`flex items-center gap-2 rounded-full px-3 h-7 transition-all duration-300 border disabled:opacity-50  disabled:pointer-events-none ${isAgentMode
                          ? "bg-primary_c border-primary_c text-white pr-6"
                          : "bg-transparent border-zinc-700 hover:bg-zinc-600 hover:text-white"
                          }`}
                      >
                        <BsRobot className="text-base" />
                        <span className="text-xs">Agents</span>
                      </button>

                      {isAgentMode && (
                        <button
                          disabled={sendTaskLoading}
                          onClick={handleTurnOffAgentMode}
                          className="absolute top-1/2 right-1 -translate-y-1/2 p-0.5 bg-black/20 hover:bg-black/40 rounded-full transition-colors  disabled:opacity-50 disabled:pointer-events-none "
                          title="Turn off Agent Mode"
                        >
                          <RxCross2 size={14} />
                        </button>
                      )}
                    </div>
                  }

                {/* Use this button to add sample prompts  */}
                <div className="relative ">
                  <button
                    onClick={() => setOpenPromptSelector(true)}
                    className={`flex items-center gap-2 rounded-full px-3 h-7 transition-all duration-300 border disabled:opacity-50  disabled:pointer-events-none 
                        bg-transparent border-zinc-500 hover:bg-zinc-600 hover:text-white`}
                  >
                    <FiMessageCircle className="text-base" />
                    <span className="text-xs">Prompts</span>
                  </button>
                </div>


                  {userInfo?.available_models?.length > 0 && (
                    <div className="relative" ref={modelSelectorRef}>
                      <button
                        disabled={sendTaskLoading}
                        onClick={() => setIsModelSelectorOpen(!isModelSelectorOpen)}
                        title="Select Language Model"
                        className="flex items-center gap-1.5 rounded-full px-3 h-7 transition-all duration-300 border bg-transparent
                      hover:bg-zinc-600 border-zinc-700 hover:text-white disabled:opacity-50 disabled:pointer-events-none"
                      >
                        <FiCpu className="text-base" />
                        <span className="text-xs truncate max-w-[100px]">
                          {currentTaskModel || "Model"}
                        </span>
                        <IoChevronUp className={`text-xs transition-transform duration-200 ${isModelSelectorOpen ? 'rotate-0' : 'rotate-180'}`} />
                      </button>
                      {isModelSelectorOpen && (
                        <div className="absolute bottom-full mb-3 w-72 
    bg-zinc-900 border border-zinc-800 
    rounded-xl shadow-2xl z-50 
    overflow-hidden backdrop-blur-md">

                          <ul className="max-h-72 overflow-y-auto py-2">

                            {/* Default Model */}
                            <li>
                              <button
                                onClick={() => {
                                  setCurrentTaskModel(null);
                                  setIsModelSelectorOpen(false);
                                }}
                                className={`w-full flex items-center justify-between px-4 py-2.5
            text-sm transition-all duration-150
            ${currentTaskModel === null
                                    ? "bg-zinc-800 text-zinc-100"
                                    : "text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"
                                  }`}
                              >
                                <div className="flex items-center gap-3">
                                  <TbBrandOpenai className="text-zinc-400 text-base" />

                                  <span className="font-medium tracking-wide">
                                    {userInfo?.default_model?.name || "Oqlous"}
                                  </span>
                                </div>

                                {currentTaskModel === null && (
                                  <FiCheck className="text-zinc-300 text-sm" />
                                )}
                              </button>
                            </li>

                            {/* Divider */}
                            <div className="my-2 border-t border-zinc-800" />

                            {/* Available Models */}
                            {userInfo.available_models.map((model, index) => (
                              <li key={model.name || index}>
                                <button
                                  onClick={() => {
                                    setCurrentTaskModel(model.name);
                                    setIsModelSelectorOpen(false);
                                  }}
                                  className={`w-full flex items-center justify-between px-4 py-2.5
              text-sm transition-all duration-150
              ${currentTaskModel === model.name
                                      ? "bg-zinc-800 text-zinc-100"
                                      : "text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"
                                    }`}
                                >
                                  <div className="flex items-center gap-3">

                                    {/* Icon via Regex */}
                                    {getModelIcon(model.name)}

                                    <div className="flex items-center gap-2">
                                      <span className="font-medium tracking-wide">
                                        {model.name}
                                      </span>

                                      {/* {model.is_new && (
                                        <span className="text-[10px] px-1.5 py-0.5 rounded 
                    bg-zinc-800 border border-zinc-700 
                    text-zinc-400 uppercase tracking-wide">
                                          New
                                        </span>
                                      )} */}
                                    </div>
                                  </div>

                                  {currentTaskModel === model.name && (
                                    <FiCheck className="text-zinc-300 text-sm" />
                                  )}
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                </div>

                {/* Mobile Tools Toggle */}
                <div className="md:hidden flex-shrink-0 relative">
                  <button
                    onClick={() => setShowMobileOptions(!showMobileOptions)}
                    className={`flex items-center gap-1 rounded-full px-2 h-7 transition-all duration-300 border ${isReasoning || isWebSearch || isCanvas || isAgentMode || showMobileOptions
                      ? "bg-zinc-700 border-zinc-700 text-white"
                      : "bg-transparent border-zinc-500 hover:bg-zinc-600 hover:text-white"
                      }`}
                  >
                    <FiSliders className="text-base" />
                    <span className="text-xs">Tools</span>
                    {(isReasoning || isWebSearch || isCanvas || isAgentMode) && (
                      <span className="bg-primary_c text-white text-xs rounded-full h-4 w-4 flex items-center justify-center ml-1">
                        {
                          [isReasoning, isWebSearch, isCanvas, isAgentMode].filter(Boolean)
                            .length
                        }
                      </span>
                    )}
                  </button>
                </div>

              </div>

              <div className="flex-shrink-0">
                <button
                className={`flex items-center rounded-full p-1.5 transition-all duration-300 border disabled:opacity-50 disabled:pointer-events-none bg-transparent border-zinc-500 hover:bg-zinc-600 hover:text-white mb-1`}
              >
                <FiPhone className="text-sm" />
              </button>
              {isStreaming ? (
                  // If streaming, show the STOP button
                  <button
                    onClick={stopStreaming}
                    className="rounded-lg h-7 w-7 flex justify-center items-center text-5xl transition-all duration-300 text-white"
                    aria-label="Stop generating response"
                  >
                    <LoadingPauseIcon />
                  </button>
                ) : !isBusy && !isloading && !createTaskLoading && !sendSubTaskLoading && !sendTaskLoading ? (
                  // If not busy, show the SEND button
                  <button
                    disabled={isloading || createTaskLoading || sendTaskLoading || isBusy || sendSubTaskLoading || ((toolCallApprovalList?.length || 0) > 0)}
                    onClick={() => {
                      // Prepend template context if a template is selected
                      let messageToSend = inputValue;
                      if (selectedTemplate) {
                        messageToSend = `[Using template: "${selectedTemplate.template_name}" (template_id: ${selectedTemplate._id})]\n${inputValue}`;
                        setSelectedTemplate(null);
                      }
                      handleSend(messageToSend);
                      setInputValue("");
                    }}
                    className={`rounded-lg h-7 w-7 flex justify-center items-center text-5xl transition-all duration-300 ${(isloading || createTaskLoading || sendTaskLoading || sendSubTaskLoading || ((toolCallApprovalList?.length || 0) > 0)) ? "bg-gradient-to-b from-[#5566FF]/50 to-[#333D99]/50" : "bg-primary-gradient"
                      }`}
                  >
                    <IoSend className="text-base" />
                  </button>
                ) : (
                  // For any other busy state, show the LOADER
                  <div className="flex items-center justify-center h-8 w-8">
                    <div style={{ transform: "scale(1.5)" }}>
                      <Loader />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {newChat &&
          <IntegrationGuideNotification
            handleShowIntegration={(e) => { e.stopPropagation(); setShowIntegrations(true); }}
          />
        }
      </div>

      {/* Modals */}
      <>
        {/* The overlay now has two different states */}
        {isDraggingOver && (
          sendTaskLoading ? (
            // 🎨 BUSY STATE UI
            <div className="absolute inset-0 bg-yellow-600 bg-opacity-70 flex flex-col items-center justify-center z-20 rounded-lg border-2 border-dashed border-white pointer-events-none">
              <FaClock className="text-white text-4xl mb-3 animate-pulse" />
              <h3 className="text-white text-lg font-semibold">Task in Progress</h3>
              <p className="text-white text-sm">Please wait to upload files.</p>
            </div>
          ) : (
            // ✅ AVAILABLE STATE UI
            <div className="absolute inset-0 bg-primary_c bg-opacity-50 flex flex-col items-center justify-center z-20 rounded-lg border-2 border-dashed border-white pointer-events-none">
              <FaFileUpload className="text-white text-4xl mb-2" />
              <p className="text-white text-lg font-semibold">Drop files here to upload</p>
            </div>
          )
        )}

        {showDropdown && filteredData.length > 0 && (
          <div
            style={{
              position: 'fixed',
              top: 'auto',
              bottom: `${window.innerHeight - dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              zIndex: 9999,
            }}
            className="w-fit max-h-60 overflow-y-auto flex flex-col justify-end"
          >
            <ul className="bg-zinc-900 border border-zinc-800 rounded-xl h-fit overflow-y-auto shadow-2xl min-w-[300px]">
              {filteredData.map((item, index) =>
                renderDropdownItem(item, index)
              )}
            </ul>
          </div>
        )}

        {/* Mobile Tools Dropup */}
        {showUploadModal && (
          <div ref={walletAddressRef} className="absolute bottom-16 z-50">
            <div className="rounded-2xl relative bg-p-text w-fit">
              <div className="side-section">
                {userGoogleId && (
                  <FileManagement
                    task_id={currentTaskId}
                    userGoogleId={userGoogleId}
                    justSavedFiles={justSavedFiles}
                    setJustSavedFiles={setJustSavedFiles}
                    setJustUploadedImages={setJustUploadedImages}
                    setTempImages={setTempImages}
                  />
                )}
              </div>
            </div>
          </div>
        )}

        {showDataSource && (
          <DataSourceModal closeModal={closeDataSourceModal} />
        )}


  
      <PromptSelectorModal
        isOpen={openPromptSelector}
        onClose={() => setOpenPromptSelector(false)}
        setInputValue={setInputValue}
        setInput={setInput}
      />
      {/* Render the Agent Architecture Modal */}
        <AgentArchitectureModal
          isOpen={isAgentModalOpen}
          onClose={() => setIsAgentModalOpen(false)}
        />

        {/* NEW: Render the McpTaskSelector Modal */}
        <McpTaskSelector
          isOpen={isMcpSelectorOpen}
          onClose={() => setIsMcpSelectorOpen(false)}
        />

        <GroupsModal
          isOpen={showGroups}
          onClose={() => setShowGroups(false)}
          openAddGroup={openModal}
        />

        <SelectFunctions
          selectedValue={selectedValue}
          isOpen={false}
          onClose={() => setShowGroups(false)}
        />

        <DataApps
          isModalOpen={isModalOpen}
          closeModal={closeModal}
          setSelectedValue={setSelectedValue}
        />
        <ExcelPreviewModal
          isOpen={excelPreviewModal.isOpen}
          onClose={closeExcelPreview}
          file={excelPreviewModal.file}
        />
        <DocxPreviewModal
          file={previewFile}
          isOpen={isPreviewOpen}
          onClose={() => setIsPreviewOpen(false)}
        />
        {showIntegrations && (
          <div className="fixed inset-0 z-50">
            <Integration onClose={() => setShowIntegrations(false)} user_google_id={userGoogleId} />
          </div>
        )}
      </>
    </>
  );
};

export default AutoResizableTextarea;